/**
 * #145 M21.2 when Fyr runs inside the iOS / iPadOS / Android app (apps/mobile), the shell injects Capacitor's bridge
 * into this page: use it for the few things a phone expects — the Android back button, outside links in the system
 * browser (not inside the app), the status bar following the theme. Nothing is bundled for it: a browser has no bridge
 * and this does nothing.
 */
interface Listener {
  remove: () => Promise<void>;
}
/** #155.3 shared into Fyr from another app's share sheet (files as base64, at most 50 MB in all). */
export interface SharedIn {
  text?: string | null;
  subject?: string | null;
  files?: { name: string; type?: string | null; data: string }[];
  truncated?: boolean;
}
/** #155.2 Capacitor's push plugin (in the app only). */
export interface PushPlugin {
  checkPermissions: () => Promise<{ receive: string }>;
  requestPermissions: () => Promise<{ receive: string }>;
  register: () => Promise<void>;
  addListener(e: 'registration', cb: (t: { value: string }) => void): Promise<Listener>;
  addListener(e: 'registrationError', cb: (err: { error: string }) => void): Promise<Listener>;
  addListener(
    e: 'pushNotificationActionPerformed',
    cb: (a: { notification: { data?: unknown } }) => void,
  ): Promise<Listener>;
}
interface Bridge {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins: {
    App?: {
      addListener(e: 'backButton', cb: (ev: { canGoBack: boolean }) => void): Promise<Listener>;
      /** #155.2 a Fyr link opened the app (universal links / App Links) */
      addListener(e: 'appUrlOpen', cb: (ev: { url: string }) => void): Promise<Listener>;
      exitApp: () => Promise<void>;
    };
    PushNotifications?: PushPlugin;
    Browser?: { open: (o: { url: string }) => Promise<void> };
    StatusBar?: { setStyle: (o: { style: 'DARK' | 'LIGHT' }) => Promise<void> };
    /** #155.1 the app's own plugin (apps/mobile): which Fyr it opens — chosen on the first run, or set by MDM */
    FyrServer?: {
      get: () => Promise<{
        server?: string;
        locked: boolean;
        managed: boolean;
        suggested: string;
        sandbox?: boolean;
      }>;
      /** #155.2 the app icon's badge (0 clears it) */
      setBadge?: (o: { count: number }) => Promise<void>;
      /** #155.3 lock after this many minutes in the background (0 = never): the organisation's policy */
      setLock?: (o: { minutes: number }) => Promise<void>;
      /** #155.3 what was shared into Fyr from another app (once): empty when nothing */
      takeShare?: () => Promise<SharedIn>;
      addListener?: (e: 'share', cb: () => void) => Promise<Listener>;
      /** forget the address: the app restarts on its "which Fyr do you use?" page */
      reset: () => Promise<void>;
    };
  };
}

export const nativeBridge = (): Bridge | null => {
  const c = (window as unknown as { Capacitor?: Bridge }).Capacitor;
  return c?.isNativePlatform?.() ? c : null;
};

/** Open a link outside Fyr in the system browser (the app keeps only Fyr and sign-in pages). */
const outside = (href: string) => {
  try {
    const u = new URL(href, location.href);
    return /^https?:$/.test(u.protocol) && u.origin !== location.origin;
  } catch {
    return false;
  }
};

export function startNative() {
  const cap = nativeBridge();
  if (!cap) return;
  document.documentElement.classList.add('native-app', `native-${cap.getPlatform?.() ?? 'app'}`);
  const { App, Browser, StatusBar } = cap.Plugins;
  void App?.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) history.back();
    else void App.exitApp();
  });
  // #155.2 Fyr links opening the app, and push (loaded only here, inside the app)
  void import('./native-push').then((m) => m.startLinks());
  if (Browser) {
    // links that open a new tab, or lead off Fyr: the system browser
    document.addEventListener(
      'click',
      (e) => {
        const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
        if (!a || e.defaultPrevented) return;
        if (a.target === '_blank' || outside(a.href)) {
          if (a.hasAttribute('download') && !outside(a.href)) return; // Fyr's own downloads stay with the WebView
          e.preventDefault();
          void Browser.open({ url: a.href });
        }
      },
      true,
    );
    const open = window.open.bind(window);
    window.open = (url?: string | URL, target?: string, features?: string) => {
      if (url && outside(String(url))) {
        void Browser.open({ url: new URL(String(url), location.href).href });
        return null;
      }
      return open(url, target, features);
    };
  }
  // the status bar's text follows Fyr's theme (light text on the dark theme)
  if (StatusBar) {
    const sync = () =>
      void StatusBar.setStyle({
        style: document.documentElement.dataset.theme === 'dark' ? 'DARK' : 'LIGHT',
      }).catch(() => undefined);
    sync();
    new MutationObserver(sync).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
  }
}
